import {
  MappedLeaveItem,
  LeaveBalancesApiResponse,
  ProcessedLeaveYear,
  AccrualPeriodLeave,
  Period,
  AccrualBalanceInfoDto,
  AccrualContractInfoDto,
} from './types';
import { isPolishB2BOrCFSEmployee } from 'domain/shared/employee';

const EXCLUDED_LEAVE_TYPES = ['PAID_LEAVE', 'SICK_DAY'] as const;

export const isCurrentOrNextPeriod = (
  data: ProcessedLeaveYear,
  currentPeriod: Period,
  nextPeriod?: Nullable<Period>,
) => {
  const itemStartDate = new Date(data.startDate);
  const itemEndDate = new Date(data.endDate);
  const currentStart = new Date(currentPeriod.startDate);
  const currentEnd = new Date(currentPeriod.endDate);
  const isCurrentPeriod =
    itemStartDate.getTime() === currentStart.getTime() && itemEndDate.getTime() === currentEnd.getTime();

  if (!nextPeriod) {
    return isCurrentPeriod;
  }

  const nextStart = new Date(nextPeriod.startDate);
  const nextEnd = new Date(nextPeriod.endDate);
  const isNextPeriod = itemStartDate.getTime() === nextStart.getTime() && itemEndDate.getTime() === nextEnd.getTime();

  return isCurrentPeriod || isNextPeriod;
};

export function calculateTaken(totalBalance?: string | number, remainedBalance?: string | number): number {
  const total = typeof totalBalance === 'string' ? parseFloat(totalBalance) : totalBalance;
  const remained = typeof remainedBalance === 'string' ? parseFloat(remainedBalance) : remainedBalance;

  if (Number.isNaN(total) || Number.isNaN(remained)) return 0;

  return (total as number) - (remained as number);
}

export const isExtended = (actualEndDate?: string, endDate?: string): boolean => actualEndDate !== endDate;

export const sortByStartDate = (a: { startDate: string | number | Date }, b: { startDate: string | number | Date }) =>
  new Date(b.startDate).getTime() - new Date(a.startDate).getTime();

export const sortLeavesYears = (years: ProcessedLeaveYear[]): ProcessedLeaveYear[] => {
  return years.sort(sortByStartDate).map(year => ({
    ...year,
    leaves: year.leaves.sort(
      (a, b) =>
        new Date(b.startDate || b.period?.startDate || '').getTime() -
        new Date(a.startDate || a.period?.startDate || '').getTime(),
    ),
  }));
};

const hasPositiveBalance = (balanceInfo?: AccrualBalanceInfoDto | null) =>
  !!balanceInfo &&
  [balanceInfo.accruedBalance, balanceInfo.remainedBalance, balanceInfo.expiredBalance].some(
    balance => Number(balance) > 0,
  );

export const getPolishContractorDisplayMode = (
  contractInfo: AccrualContractInfoDto,
  balanceInfo: AccrualBalanceInfoDto | null,
  relatedLeaves: AccrualPeriodLeave[],
) => {
  const isPolishContractor = isPolishB2BOrCFSEmployee({
    officeCode: contractInfo.office,
    contractType: contractInfo.contractType,
  });
  const hasRelatedLeaves = relatedLeaves.length > 0;
  const hasBalance = hasPositiveBalance(balanceInfo);

  return {
    isPolishContractor,
    hideBalanceColumns: isPolishContractor && !hasBalance && hasRelatedLeaves,
    skipPeriod: isPolishContractor && !hasBalance && !hasRelatedLeaves,
  };
};

export const getPreparedOtherLeaves = (data: LeaveBalancesApiResponse): MappedLeaveItem[] => {
  const allOtherLeaves = Object.values(data).flatMap(officeData =>
    Object.entries(officeData.leaveTypeBalances)
      .filter(([leaveType]) => !EXCLUDED_LEAVE_TYPES.includes(leaveType as any))
      .flatMap(([leaveType, typeBalance]) =>
        typeBalance.accrualPeriods.flatMap(period =>
          period.relatedLeaves.map((leave: AccrualPeriodLeave) => ({
            ...leave,
            leaveType,
            officeCode: period.contractInfo.office,
            contractType: period.contractInfo.contractType,
            isPolishContractor: isPolishB2BOrCFSEmployee({
              officeCode: period.contractInfo.office,
              contractType: period.contractInfo.contractType,
            }),
            startDate: leave.period.startDate,
            endDate: leave.period.plannedEndDate,
            actualEndDate: leave.period.actualEndDate,
            duration: leave.period.duration,
            isExtended: isExtended(leave.period.actualEndDate, leave.period.plannedEndDate),
            isExcludedFromBalance: leave.balanceInfo?.isExcludedFromBalance ?? false,
          })),
        ),
      ),
  );

  return allOtherLeaves.sort(
    (a, b) => new Date((b.startDate as string) || '').getTime() - new Date((a.startDate as string) || '').getTime(),
  );
};
